"use client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import {
  createCentralMonitoringReport,
  createCentralMonitoringTemplate,
  getCentralMonitoringTemplate,
} from "../../api/centralMonitoringApi";
import { getTemplates } from "../../api/reportsApi";
import { fetchStudy } from "../../api/studyApi";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import IncludedEmplyeesSection from "./IncludedEmployeesSection";
import Reports from "./Reports";
import Templates from "./Templates";
import { Skeleton } from "../../components/ui/Skeleton";
import { StudyReportsSkeleton } from "./StudySkeleton";

const SPECIALTY_MAP = [
  { key: "hematologia", accent: "#5B8FD8", badge: "bg-[#EDF3FC] text-[#3570C4]" },
  { key: "kardiologia", accent: "#2BAE72", badge: "bg-[#E6F7F1] text-[#1A8055]" },
  { key: "pulmonologia", accent: "#F4A832", badge: "bg-[#FEF3E2] text-[#C07810]" },
  { key: "neurologia", accent: "#9060D0", badge: "bg-[#F3EEF9] text-[#7040B0]" },
  { key: "onkologia", accent: "#E05050", badge: "bg-[#FEE8E8] text-[#C03030]" },
];

const TABS = ["Raporty", "Szablony", "Zespół"];

export const StudyPage = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  const { data } = useQuery(
    ["Studies", studyId!],
    () => fetchStudy(studyId!),
    {}
  );

  const { data: templates } = useQuery(
    ["Templates"],
    async () => getTemplates(studyId),
    { enabled: studyId != null, initialData: [] }
  );

  const { data: cmTemplate } = useQuery(
    ["CentralMonitoringTemplate", studyId],
    async () => {
      try {
        return await getCentralMonitoringTemplate(studyId!);
      } catch {
        return null;
      }
    },
    { enabled: !!studyId, retry: false }
  );

  const { mutate: handleNewCMReport, isLoading: isCreatingCMReport } = useMutation(
    async () => {
      if (!cmTemplate) {
        await createCentralMonitoringTemplate(studyId!);
      }
      return createCentralMonitoringReport(studyId!);
    },
    {
      onSuccess: (report) => {
        navigate(`/reports/${report.id}`);
      },
    }
  );

  useEffect(() => {
    if (data) {
      setItems([
        { text: "Badania", url: "/" } as NavigationBadge,
        { text: data.name, url: "" } as NavigationBadge,
      ]);
    }
  }, [data, setItems]);

  // Bez danych: pasek z zakładkami od razu, skeleton w miejscu nazwy i treści
  const desc = data?.description?.toLowerCase() ?? "";
  const specialties = SPECIALTY_MAP.filter(({ key }) => desc.includes(key));
  const dotColor = specialties[0]?.accent ?? "#D0D0CE";
  const reportsCount = (data?.resorts ?? []).reduce(
    (sum: number, r: any) => sum + (r.reports?.length ?? 0),
    0
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      {/* Combined header: breadcrumb + study name + tabs in one bar */}
      <div className="bg-[var(--sc-surface)] border-b border-[var(--sc-border)] h-14 flex items-center px-10 gap-4 flex-shrink-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => navigate("/")}
            className="text-[12px] text-[var(--sc-muted)] hover:text-[var(--sc-text)] transition-colors bg-transparent border-none cursor-pointer p-0"
          >
            Badania
          </button>
          <span className="text-[12px] text-[var(--sc-muted)]">›</span>
        </div>

        {/* Color dot + study name */}
        <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
          {data ? (
            <span className="text-[14px] font-semibold text-[var(--sc-text)] tracking-[-0.01em] truncate">
              {data.name}
            </span>
          ) : (
            <Skeleton className="h-4 w-44" />
          )}
          {specialties.length > 0 && (
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-[4px] flex-shrink-0 ${specialties[0].badge}`}>
              {specialties.map((s) => s.key).join(" · ")}
            </span>
          )}
        </div>

        {/* Tab bar — pushed to right */}
        <div className="flex items-stretch h-14 ml-auto">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={cn(
                "px-5 text-[13px] border-b-2 transition-colors bg-transparent border-x-0 border-t-0 outline-none cursor-pointer",
                activeTab === i
                  ? "text-[var(--sc-text)] font-medium border-[var(--sc-text)]"
                  : "text-[var(--sc-muted)] border-transparent hover:text-[var(--sc-text)]"
              )}
            >
              {tab}
              {i === 0 && data && (
                <span
                  className={cn(
                    "ml-1.5 text-[10px] px-1.5 py-0.5 rounded-[8px]",
                    activeTab === 0
                      ? "bg-[#0F0F0F15] text-[var(--sc-muted)]"
                      : "bg-[var(--sc-bg)] text-[var(--sc-muted)]"
                  )}
                >
                  {reportsCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-[var(--sc-bg)]">
        {activeTab === 0 && (data ? <Reports data={data} studyId={studyId} templates={templates} /> : <StudyReportsSkeleton />)}
        {activeTab === 1 && <Templates templates={templates} studyId={studyId!} />}
        {activeTab === 2 && <IncludedEmplyeesSection studyId={studyId!} />}
      </div>
    </div>
  );
};
